import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Pill } from "@phosphor-icons/react";

const NONE = "__none__";
const emptyRx = () => ({ name: "", dosage: "", frequency: "", duration: "", instructions: "", inventory_item_id: "", quantity: 0 });

export default function ConsultationDialog({ open, onOpenChange, appointment, onSaved }) {
  const [form, setForm] = useState({ symptoms: "", diagnosis: "", notes: "", follow_up_date: "", prescription_notes: "" });
  const [rx, setRx] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ symptoms: "", diagnosis: "", notes: "", follow_up_date: "", prescription_notes: "" });
      setRx([]);
      api.get("/inventory").then(({ data }) => setInventory(data)).catch(() => {});
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setRx((r) => r.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const invMap = useMemo(() => Object.fromEntries(inventory.map((it) => [it.id, it])), [inventory]);

  const linkInventory = (i, id) => {
    const it = invMap[id];
    setRx((r) => r.map((x, idx) => (idx === i ? { ...x, inventory_item_id: id === NONE ? "" : id, name: x.name || (it ? it.name : ""), quantity: x.quantity || 1 } : x)));
  };

  const save = async () => {
    const items = rx.filter((x) => x.name.trim()).map((x) => ({ ...x, quantity: Number(x.quantity) || 0 }));
    setBusy(true);
    try {
      await api.post("/clinic/consultations", { appointment_id: appointment.id, ...form, prescription_items: items });
      toast.success("Consultation saved");
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  if (!appointment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="consultation-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Consultation — {appointment.patient_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Symptoms / complaint</Label><Textarea rows={2} data-testid="cons-symptoms" value={form.symptoms} onChange={(e) => set("symptoms", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Diagnosis</Label><Textarea rows={2} data-testid="cons-diagnosis" value={form.diagnosis} onChange={(e) => set("diagnosis", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Clinical notes</Label><Textarea rows={3} data-testid="cons-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Stump condition, measurements, alignment, gait…" /></div>
          <div className="space-y-1.5 max-w-xs"><Label className="text-xs">Follow-up date <span className="text-muted-foreground font-normal">(auto-books)</span></Label><Input type="date" data-testid="cons-followup" value={form.follow_up_date} onChange={(e) => set("follow_up_date", e.target.value)} /></div>

          <div className="border-t border-border pt-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.15em] text-primary font-semibold flex items-center gap-1.5"><Pill size={14} /> Prescription</p>
              <Button size="sm" variant="outline" data-testid="add-rx-line" onClick={() => setRx((r) => [...r, emptyRx()])} className="gap-1.5 h-8"><Plus size={14} /> Add item</Button>
            </div>
            {rx.length === 0 ? <p className="text-xs text-muted-foreground">No prescription items. Add devices/consumables — link them to inventory to auto-debit stock on dispense.</p> : (
              <div className="space-y-3">
                {rx.map((x, i) => (
                  <div key={i} data-testid={`rx-line-${i}`} className="border border-border rounded-md p-3 space-y-2 relative">
                    <Button size="icon" variant="ghost" onClick={() => setRx((r) => r.filter((_, idx) => idx !== i))} className="absolute top-2 right-2 h-7 w-7 hover:text-destructive" data-testid={`rx-remove-${i}`}><Trash size={14} /></Button>
                    <div className="grid sm:grid-cols-2 gap-2 pr-8">
                      <div className="space-y-1"><Label className="text-xs">Item / medication</Label><Input data-testid={`rx-name-${i}`} value={x.name} onChange={(e) => setLine(i, "name", e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">Link to stock (auto-debit)</Label>
                        <Select value={x.inventory_item_id || NONE} onValueChange={(v) => linkInventory(i, v)}>
                          <SelectTrigger data-testid={`rx-inventory-${i}`}><SelectValue placeholder="Not linked" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>Not linked</SelectItem>
                            {inventory.map((it) => <SelectItem key={it.id} value={it.id}>{it.name} ({it.quantity} {it.unit})</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="space-y-1"><Label className="text-xs">Dosage</Label><Input data-testid={`rx-dosage-${i}`} value={x.dosage} onChange={(e) => setLine(i, "dosage", e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">Frequency</Label><Input value={x.frequency} onChange={(e) => setLine(i, "frequency", e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">Duration</Label><Input value={x.duration} onChange={(e) => setLine(i, "duration", e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">Qty</Label><Input type="number" step="any" data-testid={`rx-qty-${i}`} value={x.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} /></div>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Instructions</Label><Input value={x.instructions} onChange={(e) => setLine(i, "instructions", e.target.value)} /></div>
                  </div>
                ))}
                <div className="space-y-1"><Label className="text-xs">Prescription notes</Label><Input data-testid="rx-notes" value={form.prescription_notes} onChange={(e) => set("prescription_notes", e.target.value)} /></div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-consultation-button" className="bg-primary hover:bg-primary/90">{busy ? "Saving…" : "Save consultation"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
